const DEFAULT_LANGUAGE_FALLBACK = "default";

function safeLowercase(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function unique(values) {
  return values.filter((value, index) => values.indexOf(value) === index);
}

function splitBaseLanguage(locale) {
  if (!locale) return "";
  const normalized = locale.replace("_", "-");
  const separatorIndex = normalized.indexOf("-");
  if (separatorIndex === -1) return normalized;
  return normalized.slice(0, separatorIndex);
}

function getLanguagePreferenceChain() {
  const languages = [];

  if (typeof document !== "undefined" && document.documentElement) {
    const htmlLang = safeLowercase(document.documentElement.lang);
    if (htmlLang) {
      languages.push(htmlLang);
      const base = splitBaseLanguage(htmlLang);
      if (base && base !== htmlLang) {
        languages.push(base);
      }
    }
  }

  if (typeof window !== "undefined" && window.Shopify && typeof window.Shopify.locale === "string") {
    const shopifyLocale = safeLowercase(window.Shopify.locale);
    if (shopifyLocale && !languages.includes(shopifyLocale)) {
      languages.push(shopifyLocale);
      const base = splitBaseLanguage(shopifyLocale);
      if (base && !languages.includes(base)) {
        languages.push(base);
      }
    }
  }

  languages.push(DEFAULT_LANGUAGE_FALLBACK);

  return unique(languages.filter(Boolean));
}

function deepMerge(target, source) {
  if (!source || typeof source !== "object") return target;

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (sourceValue && typeof sourceValue === "object" && !Array.isArray(sourceValue)) {
      if (!targetValue || typeof targetValue !== "object" || Array.isArray(targetValue)) {
        target[key] = {};
      }
      deepMerge(target[key], sourceValue);
    } else if (sourceValue !== undefined) {
      target[key] = sourceValue;
    }
  });

  return target;
}

function getRawOverrideSources() {
  if (typeof window === "undefined") return [];

  const sources = [];

  if (window.pe_disco && typeof window.pe_disco === "object") {
    if (window.pe_disco.translation_overrides) {
      sources.push(window.pe_disco.translation_overrides);
    }
  }

  if (window.peDiscoTranslationOverrides) {
    sources.push(window.peDiscoTranslationOverrides);
  }

  return sources;
}

function getMergedOverrides() {
  const sources = getRawOverrideSources();
  if (!sources.length) return null;
  return sources.reduce((acc, source) => deepMerge(acc, source), {});
}

function getValueFromLangMap(scope, key, languageChain) {
  if (!scope || typeof scope !== "object") return undefined;

  for (let i = 0; i < languageChain.length; i += 1) {
    const lang = languageChain[i];
    if (!Object.prototype.hasOwnProperty.call(scope, lang)) continue;

    const langEntries = scope[lang];
    if (langEntries && typeof langEntries === "object" && Object.prototype.hasOwnProperty.call(langEntries, key)) {
      const value = langEntries[key];
      if (value !== undefined) {
        return value;
      }
    }
  }

  return undefined;
}

function getScopes(primary, alternatives = []) {
  const scopes = [];
  if (primary && typeof primary === "object") {
    scopes.push(primary);
  }
  if (alternatives && alternatives.length) {
    scopes.push(...alternatives.filter(Boolean));
  }
  return scopes;
}

function lookupWithFallback(scopes, key, languageChain) {
  for (let i = 0; i < scopes.length; i += 1) {
    const scope = scopes[i];
    if (!scope) continue;
    const value = getValueFromLangMap(scope, key, languageChain);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function getAnnouncementTranslation(saleId, key, fallback) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") {
    return fallback;
  }

  const announcementOverrides = overrides.announcement || overrides.announcements;
  if (!announcementOverrides || typeof announcementOverrides !== "object") {
    return fallback;
  }

  const saleIdString = saleId != null ? String(saleId) : null;
  const languageChain = getLanguagePreferenceChain();

  const saleSpecificOverrides =
    saleIdString && (announcementOverrides[saleIdString] || announcementOverrides.sales?.[saleIdString]);
  const defaultOverrides = announcementOverrides.default || announcementOverrides.defaults || announcementOverrides;

  const scopes = getScopes(saleSpecificOverrides, [defaultOverrides]);
  const value = lookupWithFallback(scopes, key, languageChain);

  return value !== undefined ? value : fallback;
}

export function getCountdownOverride(key, saleId) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") return undefined;

  const countdownOverrides = overrides.countdown || overrides.countdowns;
  if (!countdownOverrides || typeof countdownOverrides !== "object") return undefined;

  const languageChain = getLanguagePreferenceChain();

  const salesOverrides = countdownOverrides.sales || countdownOverrides.saleOverrides;
  const saleIdString = saleId != null ? String(saleId) : null;
  const saleSpecificOverrides = saleIdString && salesOverrides && salesOverrides[saleIdString];

  const globalOverrides = { ...countdownOverrides };
  delete globalOverrides.sales;
  delete globalOverrides.saleOverrides;

  const scopes = getScopes(saleSpecificOverrides, [globalOverrides]);
  return lookupWithFallback(scopes, key, languageChain);
}

export function getLanguageCandidates() {
  return getLanguagePreferenceChain();
}

export function getAnnouncementTimerOverride(key, saleId) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") return undefined;

  const timerOverrides = overrides.announcementTimer || overrides.announcement_timer;
  if (!timerOverrides || typeof timerOverrides !== "object") return undefined;

  const languageChain = getLanguagePreferenceChain();

  const salesOverrides = timerOverrides.sales || timerOverrides.saleOverrides;
  const saleIdString = saleId != null ? String(saleId) : null;
  const saleSpecificOverrides = saleIdString && salesOverrides && salesOverrides[saleIdString];

  const globalOverrides = { ...timerOverrides };
  delete globalOverrides.sales;
  delete globalOverrides.saleOverrides;

  const scopes = getScopes(saleSpecificOverrides, [globalOverrides]);
  return lookupWithFallback(scopes, key, languageChain);
}

export function getSaleTagTranslation(saleId, key, fallback) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") {
    return fallback;
  }

  const saleTagOverrides = overrides.saleTag || overrides.sale_tag;;
  if (!saleTagOverrides || typeof saleTagOverrides !== "object") {
    return fallback;
  }

  const saleIdString = saleId != null ? String(saleId) : null;
  const languageChain = getLanguagePreferenceChain();

  const saleSpecificOverrides = saleIdString && saleTagOverrides[saleIdString];
  const defaultOverrides = saleTagOverrides.default || saleTagOverrides;

  const scopes = getScopes(saleSpecificOverrides, [defaultOverrides]);
  const value = lookupWithFallback(scopes, key, languageChain);

  return value !== undefined ? value : fallback;
}

export function getHeroBannerTranslation(saleId, key, fallback) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") {
    return fallback;
  }

  const heroBannerOverrides = overrides.heroBanner || overrides.hero_banner;
  if (!heroBannerOverrides || typeof heroBannerOverrides !== "object") {
    return fallback;
  }

  const saleIdString = saleId != null ? String(saleId) : null;
  const languageChain = getLanguagePreferenceChain();

  const saleSpecificOverrides =
    saleIdString && (heroBannerOverrides[saleIdString] || heroBannerOverrides.sales?.[saleIdString]);
  const defaultOverrides = heroBannerOverrides.default || heroBannerOverrides.defaults || heroBannerOverrides;

  const scopes = getScopes(saleSpecificOverrides, [defaultOverrides]);
  const value = lookupWithFallback(scopes, key, languageChain);

  return value !== undefined ? value : fallback;
}

export function getLandingPageTranslation(saleId, key, fallback) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") {
    return fallback;
  }

  const landingPageOverrides = overrides.landingPage || overrides.landing_page;
  if (!landingPageOverrides || typeof landingPageOverrides !== "object") {
    return fallback;
  }

  const saleIdString = saleId != null ? String(saleId) : null;
  const languageChain = getLanguagePreferenceChain();

  const saleSpecificOverrides =
    saleIdString && (landingPageOverrides[saleIdString] || landingPageOverrides.sales?.[saleIdString]);
  const defaultOverrides = landingPageOverrides.default || landingPageOverrides.defaults || landingPageOverrides;

  const scopes = getScopes(saleSpecificOverrides, [defaultOverrides]);
  const value = lookupWithFallback(scopes, key, languageChain);

  return value !== undefined ? value : fallback;
}

export function getComingSoonPageTranslation(saleId, key, fallback) {
  const overrides = getMergedOverrides();
  if (!overrides || typeof overrides !== "object") {
    return fallback;
  }

  const comingSoonOverrides = overrides.comingSoonPage || overrides.coming_soon_page;
  if (!comingSoonOverrides || typeof comingSoonOverrides !== "object") {
    return fallback;
  }

  const saleIdString = saleId != null ? String(saleId) : null;
  const languageChain = getLanguagePreferenceChain();

  const saleSpecificOverrides =
    saleIdString && (comingSoonOverrides[saleIdString] || comingSoonOverrides.sales?.[saleIdString]);
  const defaultOverrides = comingSoonOverrides.default || comingSoonOverrides.defaults || comingSoonOverrides;

  const scopes = getScopes(saleSpecificOverrides, [defaultOverrides]);
  const value = lookupWithFallback(scopes, key, languageChain);

  return value !== undefined ? value : fallback;
}

/**
 * Checks if translation overrides are available.
 * Useful for React components that need to know when to re-check translations.
 */
export function hasTranslationOverrides() {
  const sources = getRawOverrideSources();
  return sources.length > 0;
}